import { Pointer } from "rfc6902";
import { Control211, Control211Props } from "../../controls/Control211";
import { useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ARST_PATCH_DATA } from "../table";
import { useArsTable } from "../table/ArsTableContextProvider";

/**
 * Props for the ArsSelect component.
 */
export interface ArsSelectProps extends Omit<Control211Props, "value" | "setValue"> {
  /** Array of options (strings or {value, label} objects) */
  options: string[] | { value: string; label: string }[];
  /** RFC 6901 JSON Pointer path to the data in ArsContext state */
  dataPath: string;
  /** Indicates if the dataPath is for table data */
  tableDataPath?: boolean;
}

/**
 * A dropdown select control that reads from and writes to ArsContext state using a JSON Pointer path.
 * This is a thin wrapper around Control211 that integrates with ArsContext.
 * Uses PATCH_ARS_STATE action to update the state value at the specified dataPath.
 *
 * @param props - Component props extending Control211Props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the data in ArsContext state.
 * @param props.options - Array of options (strings or {value, label} objects)
 * @param props.tableDataPath - Optional boolean indicating if the dataPath is for table data.
 * @returns A React functional component rendering a Control211 integrated with ArsContext.
 */
export const ArsSelect = ({
  dataPath,
  options,
  tableDataPath = false,
  ...rest
}: ArsSelectProps): React.ReactElement => {
  const ars = useArsContext();
  const table = useArsTable();
  const sourceState = tableDataPath ? (table.state?.bodyData ?? {}) : ars.state;

  // Get the current value from state using the dataPath
  const pointer = Pointer.fromJSON(dataPath);
  const value = (pointer.get(sourceState) as string) ?? "";

  // Get all available values
  const availableValues = options.map((opt) => (typeof opt === "string" ? opt : opt.value));
  // Add current value to options at the start of the list if it's not already present
  if (!availableValues.includes(value)) {
    if (typeof options[0] === "string" || options.length === 0) {
      options = [value, ...(options as string[])];
    } else {
      options = [{ value, label: value }, ...(options as { value: string; label: string }[])];
    }
  }

  /**
   * Handle selection change by dispatching PATCH_ARS_STATE action.
   *
   * @param newValue - The new value to set.
   */
  const handleChange = (newValue: string) => {
    if (newValue !== "-- None --") {
      if (!tableDataPath) {
        ars.dispatch({
          operation: PATCH_ARS_STATE,
          dataPath,
          value: newValue,
        });
      } else {
        table.dispatch({
          operation: ARST_PATCH_DATA,
          dataPath,
          value: newValue,
        });
      }
    }
  };

  return (
    <Control211
      labelWidth={2}
      options={options}
      setValue={handleChange}
      value={value}
      {...rest}
    />
  );
};

ArsSelect.displayName = "ArsSelect";
